import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, User, ChevronDown, LogOut } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import logo from "@/assets/logo.png";
import api from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/membership", label: "Membership" },
  { to: "/doorstep", label: "Doorstep" },
  { to: "/branches", label: "Branches" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [barVisible, setBarVisible] = useState(true);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setBarVisible(false);
  }, []);

  const goDashboard = () => {
    if (!user) return navigate("/login");
    if (user.role === "master_admin" || user.role === "branch_admin") navigate("/admin");
    else navigate("/dashboard");
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] }}
      data-testid="site-header"
      className="fixed inset-x-0 z-50 glass border-b border-white/5 transition-all"
      style={{ top: "32px" }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 py-4 gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 shrink-0">
          <img src={logo} alt="Hammam Logo" className="h-10 w-auto object-contain" />
          <span className="font-heading text-lg sm:text-xl text-white tracking-wider">Hammam Men Spa</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-7 flex-1 justify-center">
          {NAV.map((n) => n.dropdown ? (
            <div
              key={n.to}
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <DropdownMenu open={servicesOpen} onOpenChange={setServicesOpen}>
                <DropdownMenuTrigger data-testid={`nav-link-${n.label.toLowerCase()}`} className="text-[0.72rem] uppercase tracking-[0.18em] transition-colors text-white/70 hover:text-hgold flex items-center gap-1 outline-none nav-link-hover">
                  {n.label} <ChevronDown size={11}/>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-hpaper border-white/10 min-w-[220px]" onMouseEnter={() => setServicesOpen(true)}>
                  {n.dropdown.map((d) => (
                    <DropdownMenuItem key={d.to} onClick={() => { navigate(d.to); setServicesOpen(false); }} data-testid={`nav-dropdown-${d.label.toLowerCase().replace(/\s+/g, "-")}`} className="cursor-pointer text-white/80 focus:text-hgold focus:bg-white/5 text-sm">
                      {d.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[0.9rem] font-medium tracking-wide transition-colors nav-link-hover ${
                  n.highlight ? "text-hgold hover:text-hgold-light" : isActive ? "text-hgold active font-semibold" : "text-white/80 hover:text-hgold"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <Link to="/book" data-testid="header-book-cta" className="gold-btn whitespace-nowrap" style={{ padding: "0.55rem 1.1rem", fontSize: "0.7rem" }}>
            Book Now
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="user-menu-trigger"
                className="flex items-center gap-2.5 px-3.5 py-1.5 rounded-xl border border-amber-400/60 bg-gradient-to-r from-amber-950/40 via-black to-amber-950/20 hover:border-amber-400 text-amber-300 text-xs font-extrabold transition-all shadow-md shadow-yellow-950/30 outline-none"
              >
                <div className="w-6 h-6 rounded-lg bg-amber-400/20 border border-amber-400/60 flex items-center justify-center text-amber-300 font-heading text-xs font-bold">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <span>{user.name?.split(" ")[0] || "Account"}</span>
                <ChevronDown size={13} className="text-amber-400/80" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-gradient-to-b from-[#121212] via-black to-[#0e0e0e] border border-amber-400/60 rounded-2xl shadow-2xl p-2 min-w-[220px] space-y-1">
                <div className="px-3 py-2 border-b border-white/10 mb-1">
                  <div className="text-xs font-bold text-white truncate">{user.name}</div>
                  <div className="text-[10px] text-white/50 truncate mt-0.5">{user.email}</div>
                </div>
                <DropdownMenuItem
                  data-testid="menu-dashboard"
                  onClick={goDashboard}
                  className="cursor-pointer text-white/80 hover:text-amber-300 focus:text-amber-300 focus:bg-amber-500/20 rounded-xl px-3 py-2 text-xs font-bold transition-all flex items-center gap-2 outline-none"
                >
                  <User size={14} className="text-amber-400" /> Dashboard & VIP Console
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10 my-1" />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={logout}
                  className="cursor-pointer text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-red-950/40 rounded-xl px-3 py-2 text-xs font-bold transition-all flex items-center gap-2 outline-none"
                >
                  <LogOut size={14} /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="header-login-link" className="text-[0.72rem] uppercase tracking-[0.18em] text-white/70 hover:text-hgold">Sign In</Link>
          )}
        </div>

        <button data-testid="mobile-nav-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-white p-2">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-hblack border-t border-white/5">
          <div className="flex flex-col p-6 gap-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className={`py-1 uppercase tracking-widest text-sm ${n.highlight ? "text-hgold" : "text-white/80 hover:text-hgold"}`}>
                {n.label}
              </NavLink>
            ))}
            <div className="border-t border-white/10 pt-4 flex flex-col gap-3">
              <Link to="/book" onClick={() => setOpen(false)} className="gold-btn justify-center" data-testid="mobile-book-cta">Book Now</Link>
              {user ? (
                <button onClick={() => { goDashboard(); setOpen(false); }} data-testid="mobile-dashboard-link" className="ghost-btn justify-center">Dashboard</button>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-login-link" className="ghost-btn justify-center">Sign In</Link>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.header>
  );
}
